import React, { useState, useContext, useEffect, useRef } from 'react';
import { Context } from '../../store';
import MobileNav from './MobileNav';
import HomeScreen from './screens/HomeScreen';
import PlayerScreen from './screens/PlayerScreen';
import DonateScreen from './screens/DonateScreen';
import InfoScreen from './screens/InfoScreen';
import FavoritesScreen from './screens/FavoritesScreen';
import './mobile.css';

import bg1_mob from '../../images/bg1_mob_.png';
import bg2_mob from '../../images/bg2_mob_.png';
import bg3_mob from '../../images/bg3_mob_.png';

const MobileApp = () => {
    const [activeScreen, setActiveScreen] = useState('home');
    const prevScreenRef = useRef('home');
    const [state] = useContext(Context);

    // Track screen changes for instant transitions (not stream swipes within player)
    const isScreenChange = activeScreen !== prevScreenRef.current && 
        !(activeScreen === 'player' && prevScreenRef.current === 'player');

    // Update prevScreen after render
    useEffect(() => {
        prevScreenRef.current = activeScreen;
    }, [activeScreen]);

    // iOS PWA Safe Area Fix - directly apply styles to elements
    useEffect(() => {
        // Tag body to identify mobile app mode for index.css
        document.body.classList.add('is-mobile-app');

        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true;

        if (isStandalone) {
            const applySafeAreas = () => {
                // Find the navigation element
                const nav = document.querySelector('.mobile-nav');
                const screens = document.querySelectorAll('.mobile-screen');
                const playerWrapper = document.querySelector('.player-screen-wrapper');

                // For PWA, use fixed values that work for modern iPhones
                // Dynamic Island: ~70px padding below it, Bottom: at edge
                const safeTop = 70;
                const safeBottom = 35;

                // Apply directly to navigation
                if (nav) {
                    nav.style.bottom = `${safeBottom}px`;
                    console.log(`[PWA] Nav bottom set to ${safeBottom}px`);
                }

                // Apply to screens
                screens.forEach(screen => {
                    if (!screen.classList.contains('player-screen-wrapper')) {
                        screen.style.paddingTop = `${safeTop}px`;
                    }
                });

                // Apply to player screen
                if (playerWrapper) {
                    playerWrapper.style.paddingTop = `${safeTop}px`;
                }

                // Stretch background layer position in PWA
                const bgLayer = document.querySelector('.mobile-background-layer');
                if (bgLayer) {
                    bgLayer.style.top = '0';
                    bgLayer.style.bottom = '0';
                    bgLayer.style.left = '0';
                    bgLayer.style.right = '0';
                    console.log('[PWA] Background layer stretched');
                }

                console.log(`[PWA] Safe areas applied: top=${safeTop}px, bottom=${safeBottom}px`);
            };

            // Apply after DOM is ready and with delays
            if (document.readyState === 'complete') {
                applySafeAreas();
            } else {
                window.addEventListener('load', applySafeAreas);
            }
            setTimeout(applySafeAreas, 100);
            setTimeout(applySafeAreas, 500);
            setTimeout(applySafeAreas, 1000);

            // Re-apply on orientation change
            window.addEventListener('orientationchange', () => {
                setTimeout(applySafeAreas, 100);
            });
        }

        return () => {
            document.body.classList.remove('is-mobile-app');
        };
    }, []);

    // Предзагрузка фонов
    useEffect(() => {
        const images = [bg1_mob, bg2_mob, bg3_mob];
        images.forEach(src => {
            const img = new Image();
            img.src = src;
        });
    }, []);

    // Safari-compatible Background Switching via CSS Classes
    useEffect(() => {
        // Stream to CSS theme class mapping
        const streamThemes = {
            myata: 'theme-blue',
            gold: 'theme-blue',
            myata_hits: 'theme-blue',
            xtra: 'theme-blue'
        };

        // Stream to theme color mapping (for meta tag)
        const streamColors = {
            myata: '#1C3F5F',
            gold: '#1C3F5F',
            myata_hits: '#1C3F5F',
            xtra: '#1C3F5F'
        };

        // Base theme for non-player screens
        const baseTheme = 'theme-blue';
        const baseColor = '#1C3F5F';

        // Determine active theme and color
        const activeTheme = (activeScreen === 'player') 
            ? (streamThemes[state.stream] || baseTheme) 
            : baseTheme;
        const activeColor = (activeScreen === 'player') 
            ? (streamColors[state.stream] || baseColor) 
            : baseColor;

        // --- CSS CLASS-BASED THEME SWITCHING ---
        if (!document.body.classList.contains('is-mobile-app')) return;

        // Remove all theme classes first
        document.body.classList.remove('theme-blue', 'theme-purple', 'theme-gold', 'theme-teal', 'theme-info');
        document.documentElement.classList.remove('theme-blue', 'theme-purple', 'theme-gold', 'theme-teal', 'theme-info');
        
        // Add new theme class
        document.body.classList.add(activeTheme);
        document.documentElement.classList.add(activeTheme);

        // Update Theme Color meta tag for browser chrome
        let meta = document.getElementById('theme-color-meta');
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'theme-color';
            meta.id = 'theme-color-meta';
            document.head.appendChild(meta);
        }
        meta.content = activeColor;

    }, [activeScreen, state.stream]);

    const getActiveBg = () => {
        if (activeScreen === 'player') {
            const st = state.stream || 'myata';
            return st === 'myata_hits' ? 'xtra' : st;
        }
        return 'base';
    };

    const activeBg = getActiveBg();

    return (
        <div className={`mobile-app bg-${activeBg}`}>
            {/* Multi-layer Background System for Instant Switching */}
            <div className={`mobile-background-layer bg-layer-base ${activeBg === 'base' ? 'active' : ''} ${isScreenChange ? 'no-transition' : ''}`} />
            <div className={`mobile-background-layer bg-layer-myata ${activeBg === 'myata' ? 'active' : ''} ${isScreenChange ? 'no-transition' : ''}`} />
            <div className={`mobile-background-layer bg-layer-gold ${activeBg === 'gold' ? 'active' : ''} ${isScreenChange ? 'no-transition' : ''}`} />
            <div className={`mobile-background-layer bg-layer-xtra ${activeBg === 'xtra' ? 'active' : ''} ${isScreenChange ? 'no-transition' : ''}`} />

            <div className="mobile-content">
                {/* Each screen wrapper must have flex: 1 to maintain correct layout */}
                <div className={`mobile-screen-container ${activeScreen === 'home' ? '' : 'screen-hidden'}`}>
                    <HomeScreen onNavigate={setActiveScreen} />
                </div>

                <div className={`mobile-screen-container ${activeScreen === 'player' ? '' : 'screen-hidden'}`}>
                    <PlayerScreen isVisible={activeScreen === 'player'} />
                </div>

                <div className={`mobile-screen-container ${activeScreen === 'donate' ? '' : 'screen-hidden'}`}>
                    <DonateScreen />
                </div>

                <div className={`mobile-screen-container ${activeScreen === 'info' ? '' : 'screen-hidden'}`}>
                    <InfoScreen />
                </div>

                <div className={`mobile-screen-container ${activeScreen === 'favorites' ? '' : 'screen-hidden'}`}>
                    <FavoritesScreen />
                </div>
            </div>

            <MobileNav activeScreen={activeScreen} onNavigate={setActiveScreen} />
        </div>
    );
};

export default MobileApp;
